import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {UntilDestroy} from '@ngneat/until-destroy';
import {EMPTY, Subject, Subscription} from 'rxjs';
import {catchError, delay, distinctUntilChanged, finalize, tap} from 'rxjs/operators';
import {Moment} from 'moment';
import {
  BusinessNumberSearchFn,
  getEntityType,
  formControlErrorKeys,
  formControlErrorMessage,
} from '@portal-workspace/grow-ui-library';
import {setupUntilDestroy, CustomErrorStateMatcher} from '@portal-workspace/grow-ui-library';
import {
  AzureStorageDocument,
  UploadedDocumentRef,
  BusinessNumberSearchValue,
  compareMatch,
  EntityGuarantor,
  EntityTypeCompanyOption,
  EntityTypes,
  EntityTypeValue,
  EntityTypeValueOptions,
  ExistingPersonRef,
  GuarantorKybSelectionsEvent,
  GuarantorValue,
  IndividualGuarantor,
  IndividualGuarantorWithoutAddress,
  IndividualOrEntity,
  KYB_ADDED_GUARANTOR_LOCK_TOOLTIP,
  KYB_ADDED_GUARANTOR_SIGNER_TOOLTIP,
  KybAddedParties,
  KybDisplayStatus,
  KybEntityResult,
  KybIndividualTrusteeDialogData,
  KybInitiateCorporatePartyFn,
  KybKeyPeopleValue,
  KybManualEntityValue,
  KybManualPerson,
  KybPerson,
  KybPersonRoleSelection,
  KybRefreshFn,
  KybResult,
  KybResultFn,
  KybSetManualModeFn,
  NotNullable,
  PREVIOUS_BUSINESS_ENTITY_SOURCE,
  kybDigitsOnly,
  kybDisplayStatus,
  kybGuarantorsHeldByKeyPeople,
  kybHasOwnKeyPeople,
  kybIsAdditionalGuarantor,
  kybIsCorporateParty,
  kybIsSoleTraderRoleLocked,
  kybResolveSelected,
  kybValidateCompanyKeyPeople,
} from '@portal-workspace/grow-shared-library';
import {AbstractControlValueAccessor} from '../abstract-control-value-accessor';
import {MARK, Mark} from '@portal-workspace/grow-ui-library/mark';
import {EntityTypeComponent} from '../entity-type-component/entity-type.component';
import {BusinessNumberSearchComponent} from '../business-number-search-component/business-number-search.component';
import {MatButtonModule} from '@angular/material/button';
import {MarkDirective} from '../../directives/mark-as-dirty.directive';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MessageBoxComponent} from '../message-box/message-box.component';
import {KybAddedPartiesComponent, KybAddedPartiesEvent} from '../kyb-key-people-component/kyb-added-parties.component';
import {KybEntitySectionComponent} from '../kyb-key-people-component/kyb-entity-section.component';
import {KybLockTickComponent} from '../kyb-key-people-component/kyb-lock-tick.component';
import {KybStatusComponent} from '../kyb-key-people-component/kyb-status.component';
import {ApplicationDialogService} from '../application-dialog-component/application-dialog.service';
import {ManualKybPeopleComponent} from '../kyb-key-people-component/manual-kyb-people.component';
import {IndividualIdDocumentUpload} from '../component-utils';

export interface GuarantorComponentEvent {
  entries: (IndividualGuarantor | EntityGuarantor)[];
}

export type IndividualOrEntityFormGroupType = FormGroup<{
  type: FormControl<'Individual' | 'Entity' | null>,

  // entity
  organisation?: FormControl<NotNullable<EntityGuarantor>['organisation'] | null>,
  organisationType?: FormControl<NotNullable<EntityGuarantor>['organisationType'] | null>,
  abn?: FormControl<string | null>,
  acn?: FormControl<string | null>,
  // Set once the guarantor has been persisted by the company check, which is what ties the row to
  // its KYB run and its key people.
  commercialEntityId?: FormControl<number | null>,

  // individual
  firstName?: FormControl<string | null>,
  middleName?: FormControl<string | null>,
  lastName?: FormControl<string | null>,
  dob?: FormControl<Moment | null>,
}>;
export type IndividualOrEntityFormArrayType = FormArray<IndividualOrEntityFormGroupType>;

@UntilDestroy({arrayName: 'subscriptions'})
@Component({
  selector: 'guarantor',
  templateUrl: './guarantor.component.html',
  styleUrls: ['./guarantor.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => GuarantorComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => GuarantorComponent), multi: true},
    {provide: MARK, useExisting: forwardRef(() => GuarantorComponent)},
  ],
  standalone: true,
  imports: [
    MessageBoxComponent, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule,
    MatButtonToggleModule, MarkDirective, MatButtonModule, BusinessNumberSearchComponent,
    EntityTypeComponent, KybAddedPartiesComponent, KybEntitySectionComponent, KybLockTickComponent,
    KybStatusComponent, ManualKybPeopleComponent,
  ],
})
export class GuarantorComponent extends AbstractControlValueAccessor<GuarantorValue>
  implements OnInit, OnChanges, AfterViewInit, Mark, Validator {

  errorKeys = formControlErrorKeys;
  errorMessage = formControlErrorMessage;

  markObservable: Subject<boolean> = new Subject<boolean>();
  errorStateMatcher = new CustomErrorStateMatcher();

  formGroup: FormGroup<{ guarantors: IndividualOrEntityFormArrayType }>;
  formArray: IndividualOrEntityFormArrayType;

  subscriptions: Subscription[] = [];

  @Input({required: true}) businessNumberSearchFn!: BusinessNumberSearchFn;
  @Input({required: false}) applicationId: number | null = null;
  @Input({required: false}) uploadedDocuments: AzureStorageDocument[] | null = null;
  @Input({required: false}) kybResultFn: KybResultFn | null = null;
  // Backs the Retry inside the waiting panel: pulls the execution from FrankieOne rather than
  // starting a second, billable one.
  @Input({required: false}) kybRefreshFn: KybRefreshFn | null = null;
  @Input({required: false}) kybSetManualModeFn: KybSetManualModeFn | null = null;
  // Persists the guarantor entity and starts its company check, so its directors and shareholders
  // can be ticked while the broker is still on the step.
  @Input({required: false}) kybInitiateCorporatePartyFn: KybInitiateCorporatePartyFn | null = null;
  /**
   * What the Key People step currently has ticked, so a guarantor named there is carried onto the
   * same individual here (see mirrorKeyPeopleGuarantors). Read live rather than from the KYB result,
   * so a tick the broker has just cleared on that step does not leave a locked one behind here.
   * Direct sale leaves it null: it has no Key People step.
   */
  @Input({required: false}) keyPeopleSelections: KybKeyPeopleValue | null = null;
  /**
   * Renders one guarantor and nothing around it: no step headings, no add/remove, and no adopting of
   * guarantor entities the application already holds. For a host that is itself the act of adding a
   * single guarantor - the KYC/AML tab's Add Guarantor dialog - where the guarantors already on the
   * application are listed elsewhere on the page and repeating them here would only invite the
   * reviewer to re-enter one.
   */
  @Input({required: false}) singleEntry = false;

  @Output() events = new EventEmitter<GuarantorComponentEvent>();
  @Output() kybSelectionsChange = new EventEmitter<GuarantorKybSelectionsEvent>();
  @Output() idDocumentDelete = new EventEmitter<UploadedDocumentRef>();
  // Non-blocking warnings (sub-50% guarantee coverage), shown by the page as a confirmation when
  // Next is pressed, matching the Key People step.
  @Output() warningsChange = new EventEmitter<string[]>();
  @ViewChildren(ManualKybPeopleComponent) manualPeopleComponents!: QueryList<ManualKybPeopleComponent>;

  entityTypeReadonly = true;
  kybResult: KybResult | null = null;

  addedGuarantorLockTooltip = KYB_ADDED_GUARANTOR_LOCK_TOOLTIP;
  addedGuarantorSignerTooltip = KYB_ADDED_GUARANTOR_SIGNER_TOOLTIP;

  /** Direct sale has no KYB step, so the entity guarantor there is details-only. */
  get kybEnabled(): boolean {
    return !!this.kybInitiateCorporatePartyFn && this.applicationId != null;
  }

  // Guarantor/signer ticks keyed by stakeholder, mirroring the Key People step's write model.
  private selectionByStakeholder = new Map<number, KybPersonRoleSelection>();
  private selectedStakeholderIds = new Set<number>();
  // Stakeholders whose persisted state has already been read into the two maps above. Seeding is
  // once per stakeholder so a later read of the KYB result - a poll, or another guarantor's check
  // landing - cannot resurrect a tick the broker has since cleared.
  private seededStakeholderIds = new Set<number>();
  // People of a sole-trader guarantor, whose guarantor and signer ticks are locked on.
  private soleTraderStakeholderIds = new Set<number>();
  // Stakeholders on this step whose guarantor tick is held by the Key People step.
  private guarantorLockedIds = new Set<number>();
  private entityErrors: string[] = [];
  // Errors stay hidden until Next is pressed (which reaches mark() through the stepper's mark
  // trigger), so ticking boxes never raises one. From that point on they update live, because
  // recomputeKybValidation re-runs on every change. Matches the Key People step.
  private showValidation = false;
  // The ABN/ACN each row was last initiated with, so a row that re-emits does not re-fire while a
  // row whose company changed still does. Held per row rather than globally: a removed and re-added
  // guarantor is a new row that needs its own entity.
  private initiatedIdentifiers = new Map<IndividualOrEntityFormGroupType, string>();
  // Latest state of the trustee/partner subtree under each trust/partnership guarantor row.
  private partiesByFormGroup = new Map<IndividualOrEntityFormGroupType, KybAddedPartiesEvent>();
  private manualPeopleByEntity = new Map<number, KybManualPerson[]>();
  private manualValidityByEntity = new Map<number, boolean>();
  // Broker has typed at least one person against this entity. A later KYB result must not put
  // persisted people back after they have cleared the list.
  private manualPeopleEnteredEntityIds = new Set<number>();
  private loadingKybApplicationId: number | null = null;
  private onValidatorChange: () => void = () => {};
  // Guarantor entities the broker removed on this step. adoptUnlistedGuarantorEntities cannot tell a
  // company that was never listed from one that was just removed - both are simply absent from the
  // form - so without this it puts the removed guarantor back on the next read of the KYB result.
  // Held by identity as well as id, because a row removed before its entity was reconciled has no id
  // to remember. Cleared when the step is rewritten, by which point the save has removed the entity.
  private removedGuarantorKeys = new Set<string>();

  constructor(
    private formBuilder: FormBuilder,
    private dialogService: ApplicationDialogService,
  ) {
    super();
    this.formArray = formBuilder.array<IndividualOrEntityFormGroupType>([], [this.additionalGuarantorsValidator()]);
    this.formGroup = formBuilder.group({guarantors: this.formArray});
  }

  ngOnInit(): void {
    setupUntilDestroy(this);
    this.subscriptions.push(this.formArray.valueChanges.pipe(
      delay(0),
      distinctUntilChanged(compareMatch),
      tap(() => this.changes()),
    ).subscribe());
  }

  /**
   * A new application has no id until its first save, and the pages bind 0 in the meantime, so the
   * read made when the form was written found nothing. Re-read once the real id arrives - that read
   * is also what brings in an entity created for an earlier step; see adoptUnlistedGuarantorEntities.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['applicationId'] || changes['kybResultFn']) && this.applicationId && this.kybResultFn) {
      this.loadKybResult();
    }
    if (changes['keyPeopleSelections'] && !changes['keyPeopleSelections'].firstChange) {
      // Deferred because the page reads this binding during its own change detection pass, while
      // changes() writes this step's value back: doing that synchronously would alter a binding the
      // page has already rendered.
      setTimeout(() => this.changes());
    }
  }

  ngAfterViewInit() {
    setTimeout(() => this.changes());
  }

  /**
   * The step is optional, so an empty list is valid. A started entry is not: a half-entered
   * guarantor would otherwise be silently dropped by the save mapping.
   */
  private additionalGuarantorsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formArray = control as IndividualOrEntityFormArrayType;
      const incomplete = formArray.controls.some(formGroup => !this.isEntryComplete(formGroup));
      if (incomplete) {
        return {incompleteGuarantor: true};
      }
      return this.entityErrors.length ? {kybKeyPeople: {details: this.entityErrors}} : null;
    };
  }

  private isEntryComplete(formGroup: IndividualOrEntityFormGroupType): boolean {
    if (formGroup.controls.type?.value === 'Individual') {
      return !!(formGroup.controls.firstName?.value && formGroup.controls.lastName?.value && formGroup.controls.dob?.value);
    }
    if (!this.kybEnabled) {
      // Direct sale runs no company check, so the entity's own details are all this step captures.
      return formGroup.valid;
    }
    // A trust or partnership has no officers of its own to wait for: its key people are the
    // trustees/partners entered against it, so completeness is theirs to report.
    if (this.partiesEntityType(formGroup)) {
      return !!this.partiesByFormGroup.get(formGroup)?.valid;
    }
    // An entity is only complete once its company check has landed, because the signer selection
    // it gates cannot be made before then.
    const entity = this.entityFor(formGroup);
    return !!entity && (entity.mode === 'MANUAL'
      ? !!this.manualValidityByEntity.get(entity.commercialEntityId)
      : kybDisplayStatus(entity.status) === 'resolved');
  }

  /**
   * The type whose parties this row collects, or null when its key people come from its own company
   * check. Read off the row's ABN/ACN lookup rather than the KYB result, which carries no type for a
   * trust or partnership because FrankieOne is never asked about one.
   */
  partiesEntityType(formGroup: IndividualOrEntityFormGroupType): EntityTypes | null {
    if (formGroup.controls.type?.value !== 'Entity') {
      return null;
    }
    const entityType = formGroup.controls.organisationType?.value?.type ?? null;
    return entityType && !kybHasOwnKeyPeople(entityType) ? entityType : null;
  }

  onPartiesChanged(formGroup: IndividualOrEntityFormGroupType, event: KybAddedPartiesEvent): void {
    this.partiesByFormGroup.set(formGroup, event);
    // A party row was removed, so the ticks made against its people go with it: the save clears the
    // roles of anyone it no longer receives, and would otherwise write roles for a soft-deleted entity.
    for (const stakeholderId of event.removedStakeholderIds) {
      this.forgetPerson(stakeholderId);
    }
    this.changes();
  }

  guarantorFormGroups(): IndividualOrEntityFormGroupType[] {
    return this.formArray.controls;
  }

  guarantorFormGroupFormControl(formGroup: FormGroup, controlName: string): FormControl {
    return formGroup.controls[controlName] as FormControl;
  }

  // ---------------------------------------------------------------- value

  private changes(): void {
    for (const formGroup of this.formArray.controls) {
      this.ensurePartiesEntity(formGroup);
      this.normalizeAcnControl(formGroup);
    }
    this.seedSelections();
    this.mirrorKeyPeopleGuarantors();
    this.recomputeKybValidation();
    this.formArray.updateValueAndValidity({emitEvent: false});
    // Validation is reported through NG_VALIDATORS. Keep the entered value so a nested error is not
    // disguised as the parent control's `requiredAllowEmpty` error.
    this.propagateChange(this.toGuarantorValue());
    this.onValidatorChange();
    this.kybSelectionsChange.emit({
      selections: [...this.selectionByStakeholder.values()],
      selectedStakeholderIds: [...this.selectedStakeholderIds],
      addedParties: this.collectAddedParties(),
      manualEntities: this.manualEntityValues(),
    });
  }

  private normalizeAcnControl(formGroup: IndividualOrEntityFormGroupType): void {
    const control = formGroup.controls.acn;
    const acn = kybDigitsOnly(control?.value);
    if (control && acn && acn.length < 9) {
      control.setValue(acn.padStart(9, '0'), {emitEvent: false});
    }
  }

  private manualEntityValues(): KybManualEntityValue[] {
    return this.renderedEntities()
      .filter(entity => entity.mode === 'MANUAL')
      .map(entity => ({
        commercialEntityId: entity.commercialEntityId,
        people: this.manualPeopleByEntity.get(entity.commercialEntityId) ?? entity.manualPeople,
      }));
  }

  // Driven off the live rows rather than the map, so a removed guarantor takes its parties with it.
  private collectAddedParties(): KybAddedParties[] {
    return this.formArray.controls.flatMap(
      formGroup => this.partiesByFormGroup.get(formGroup)?.addedParties ?? [],
    );
  }

  private toGuarantorValue(): NotNullable<GuarantorValue> {
    const value: NotNullable<GuarantorValue> = [];
    for (const formGroup of this.formArray.controls) {
      if (formGroup.controls.type?.value === 'Individual') {
        // Only the identity is captured here; the rest of the individual's details are collected
        // on the Individual details step, which reads them back from the saved rows.
        const individual: IndividualGuarantorWithoutAddress = {
          type: 'Individual',
          kind: 'WithoutPropertyAddress',
          title: null,
          firstName: formGroup.controls.firstName?.value ?? '',
          middleName: formGroup.controls.middleName?.value ?? '',
          lastName: formGroup.controls.lastName?.value ?? '',
          dob: formGroup.controls.dob?.value as Moment,
          residentialAddress: null,
          propertyOwner: null,
          email: '',
          mobileNumber: '',
          personalAssets: null,
          personalLiabilities: null,
        };
        value.push(individual);
      } else if (formGroup.controls.type?.value === 'Entity') {
        value.push({
          type: 'Entity',
          organisation: formGroup.controls.organisation?.value as NotNullable<EntityGuarantor>['organisation'],
          organisationType: formGroup.controls.organisationType?.value as NotNullable<EntityGuarantor>['organisationType'],
          abn: formGroup.controls.abn?.value ?? '',
          acn: formGroup.controls.acn?.value ?? '',
        });
      }
    }
    return value;
  }

  doWriteValue(v?: GuarantorValue): void {
    this.removedGuarantorKeys.clear();
    this.formArray.clear({emitEvent: false});
    for (const guarantor of v ?? []) {
      this.addGuarantor(guarantor);
    }
    // The single-entry host has no Add button, so the one row it shows has to be there from the
    // start.
    if (this.singleEntry && !this.formArray.length) {
      this.addGuarantor();
    }
    this.loadKybResult();
  }

  // ---------------------------------------------------------------- add / remove / switch

  addGuarantor(g?: NotNullable<GuarantorValue>[number]): void {
    // Entity first: an additional guarantor is far more often a company than a person, and the AC
    // asks for the ABN field to be reachable without a further click.
    const formControlType = this.formBuilder.control<IndividualOrEntity | null>(g ? g.type : 'Entity', [Validators.required]);
    const formGroup: IndividualOrEntityFormGroupType = this.formBuilder.group({type: formControlType});

    if (g?.type === 'Individual') {
      this.switchToIndividual(formGroup, g);
    } else {
      this.switchToEntity(formGroup, g?.type === 'Entity' ? g : undefined);
    }
    this.formArray.push(formGroup);

    this.subscriptions.push(formControlType.valueChanges.pipe(
      tap(async type => {
        if (type === 'Individual') {
          if (await this.confirmDiscard(formGroup, 'Entity')) {
            this.switchToIndividual(formGroup);
          } else {
            formControlType.setValue('Entity', {emitEvent: false});
          }
        } else if (type === 'Entity') {
          if (await this.confirmDiscard(formGroup, 'Individual')) {
            this.switchToEntity(formGroup);
          } else {
            formControlType.setValue('Individual', {emitEvent: false});
          }
        }
      }),
    ).subscribe());

    this.events.emit({entries: this.toGuarantorValue()});
  }

  /**
   * ADO-827: an ABN materialised from the application's Previous Business (see
   * adoptUnlistedGuarantorEntities) is not this step's to discard - it was captured on step 1 and
   * stays linked to the application regardless of what the broker does here.
   */
  canRemoveGuarantor(formGroup: IndividualOrEntityFormGroupType): boolean {
    return this.entityFor(formGroup)?.source !== PREVIOUS_BUSINESS_ENTITY_SOURCE;
  }

  removeGuarantor(formGroup: IndividualOrEntityFormGroupType): void {
    // Both taken while the row is still there, since its entity and its ticks are only reachable
    // through it. A tick left behind would be saved against a guarantor that is no longer on the
    // application, and an unremembered entity would be adopted straight back onto the form.
    this.forgetEntitySelections(this.tickedEntitiesFor(formGroup));
    this.rememberRemovedGuarantor(formGroup);
    const index = this.formArray.controls.indexOf(formGroup);
    if (index !== -1) {
      this.formArray.removeAt(index);
    }
    this.discardParties(formGroup);
    this.initiatedIdentifiers.delete(formGroup);
    this.events.emit({entries: this.toGuarantorValue()});
    this.changes();
  }

  /** The entity a removed row named, by whichever of the three handles the row was carrying. */
  private rememberRemovedGuarantor(formGroup: IndividualOrEntityFormGroupType): void {
    const commercialEntityId = formGroup.controls.commercialEntityId?.value;
    if (commercialEntityId != null) {
      this.removedGuarantorKeys.add(`id:${commercialEntityId}`);
    }
    const abn = formGroup.controls.abn?.value;
    if (abn) {
      this.removedGuarantorKeys.add(`abn:${abn}`);
    }
    const acn = formGroup.controls.acn?.value;
    if (acn) {
      this.removedGuarantorKeys.add(`acn:${acn}`);
    }
  }

  private isRemovedGuarantor(entity: KybEntityResult): boolean {
    return this.removedGuarantorKeys.has(`id:${entity.commercialEntityId}`)
      || (!!entity.abn && this.removedGuarantorKeys.has(`abn:${entity.abn}`))
      || (!!entity.acn && this.removedGuarantorKeys.has(`acn:${entity.acn}`));
  }

  /**
   * Forget the trustees/partners of a row that is no longer a trust or partnership guarantor, along
   * with the ticks made against their people. The parties are the guarantor's, so leaving them
   * behind would save trustees for a guarantor that no longer exists.
   */
  private discardParties(formGroup: IndividualOrEntityFormGroupType): void {
    const parties = this.partiesByFormGroup.get(formGroup);
    if (!parties) {
      return;
    }
    this.forgetEntitySelections(parties.corporateParties);
    this.partiesByFormGroup.delete(formGroup);
  }

  /** Drop the ticks held for the key people of entities that have left the screen. */
  private forgetEntitySelections(entities: readonly KybEntityResult[]): void {
    for (const entity of entities) {
      for (const person of entity.people) {
        this.forgetPerson(person.stakeholderId);
      }
    }
  }

  private forgetPerson(stakeholderId: number): void {
    this.selectionByStakeholder.delete(stakeholderId);
    this.selectedStakeholderIds.delete(stakeholderId);
    // Forgotten as unseeded too, so the same company entered again is read from the KYB result
    // afresh rather than rendering ticks this step no longer reports.
    this.seededStakeholderIds.delete(stakeholderId);
  }

  /**
   * Switching type throws away whatever was entered, so it is only silent while there is nothing
   * to lose. A completed company check is treated as data: the run stays on the entity and is
   * reused if the same ABN comes back within the cooldown, but the selection made against it goes.
   */
  private async confirmDiscard(formGroup: IndividualOrEntityFormGroupType, from: IndividualOrEntity): Promise<boolean> {
    const hasData = from === 'Entity'
      ? !!(formGroup.controls.organisation?.value || formGroup.controls.abn?.value || formGroup.controls.commercialEntityId?.value)
      : !!(formGroup.controls.firstName?.value || formGroup.controls.lastName?.value || formGroup.controls.dob?.value);
    if (!hasData) {
      return true;
    }
    return !!await this.dialogService.openConfirmationDialog({
      message: 'Discard details?',
      subMessage: 'The details you entered for this guarantor will be discarded.',
    }).afterClosed().toPromise().then(r => r?.readyForSubmission);
  }

  private switchToIndividual(formGroup: IndividualOrEntityFormGroupType, g?: IndividualGuarantor): void {
    // The row no longer shows the company's key people, so the ticks made against them are not this
    // guarantor's any more.
    this.forgetEntitySelections(this.tickedEntitiesFor(formGroup));
    this.discardParties(formGroup);
    // The row's entity link goes with its entity controls, so the same company entered again later
    // has to be initiated afresh.
    this.initiatedIdentifiers.delete(formGroup);
    formGroup.removeControl('organisation');
    formGroup.removeControl('organisationType');
    formGroup.removeControl('abn');
    formGroup.removeControl('acn');
    formGroup.removeControl('commercialEntityId');

    formGroup.addControl('firstName', this.formBuilder.control(g?.firstName ?? null, [Validators.required]));
    formGroup.addControl('middleName', this.formBuilder.control(g?.middleName ?? null));
    formGroup.addControl('lastName', this.formBuilder.control(g?.lastName ?? null, [Validators.required]));
    formGroup.addControl('dob', this.formBuilder.control<Moment | null>(g?.dob ?? null, [Validators.required]));
  }

  private switchToEntity(formGroup: IndividualOrEntityFormGroupType, g?: EntityGuarantor): void {
    const formControlOrganisation = this.formBuilder.control(g?.organisation ?? null, [Validators.required]);
    const formControlOrganisationType = this.formBuilder.control(g?.organisationType ?? null, [Validators.required]);
    const formControlAbn = this.formBuilder.control<string | null>(g?.abn ?? null);
    const formControlAcn = this.formBuilder.control<string | null>(g?.acn ?? null);

    this.subscriptions.push(formControlOrganisation.valueChanges.pipe(
      tap((r: BusinessNumberSearchValue) => {
        if (!r) {
          return;
        }
        const acn = r.acn ? r.acn.padStart(9, '0') : '';
        formControlAcn.setValue(acn);
        formControlAbn.setValue(r.abn ?? '');
        if (r.result) {
          const entityType = getEntityType(r.result);
          formControlOrganisationType.setValue(entityType);
          this.entityTypeReadonly = entityType?.type !== 'other';
        } else {
          this.entityTypeReadonly = !!formControlOrganisationType.value;
        }
      }),
    ).subscribe());

    this.subscriptions.push(formControlOrganisationType.valueChanges.pipe(
      tap((r: EntityTypeValue) => {
        // The row no longer collects parties (a different ABN was searched, or the type was
        // corrected by hand), so the trustees/partners entered under the old type are not this
        // guarantor's and must not be saved against it.
        if (kybHasOwnKeyPeople(r?.type ?? null)) {
          this.discardParties(formGroup);
        }
        if (r?.type === 'company') {
          formControlAcn.setValidators([Validators.required, Validators.minLength(9)]);
        } else if (r?.type === 'trust') {
          formControlAcn.setValidators([Validators.minLength(9)]);
        } else {
          formControlAcn.clearValidators();
        }
        formControlAcn.updateValueAndValidity();
      }),
    ).subscribe());

    formGroup.removeControl('firstName');
    formGroup.removeControl('middleName');
    formGroup.removeControl('lastName');
    formGroup.removeControl('dob');

    formGroup.addControl('organisation', formControlOrganisation);
    formGroup.addControl('organisationType', formControlOrganisationType);
    formGroup.addControl('abn', formControlAbn);
    formGroup.addControl('acn', formControlAcn);
    formGroup.addControl('commercialEntityId', this.formBuilder.control<number | null>(null));
  }

  // ---------------------------------------------------------------- individual popup

  /** Empty until the popup has been filled in, which is what keeps the row hidden until then. */
  individualName(formGroup: IndividualOrEntityFormGroupType): string {
    const firstName = formGroup.controls.firstName?.value ?? '';
    const lastName = formGroup.controls.lastName?.value ?? '';
    return `${firstName} ${lastName}`.trim();
  }

  openIndividualDialog(formGroup: IndividualOrEntityFormGroupType): void {
    const data: KybIndividualTrusteeDialogData = {
      title: 'Add Individual',
      firstName: formGroup.controls.firstName?.value ?? null,
      middleName: formGroup.controls.middleName?.value ?? null,
      lastName: formGroup.controls.lastName?.value ?? null,
      dob: formGroup.controls.dob?.value ?? null,
      existingPeople: this.peopleAlreadyOnApplication(formGroup),
    };
    this.subscriptions.push(this.dialogService.openKybIndividualTrusteeDialog(data).afterClosed().pipe(
      tap(result => {
        if (!result) {
          return;
        }
        formGroup.controls.firstName?.setValue(result.firstName);
        formGroup.controls.middleName?.setValue(result.middleName);
        formGroup.controls.lastName?.setValue(result.lastName);
        formGroup.controls.dob?.setValue(result.dob);
        formGroup.markAllAsTouched();
        this.changes();
      }),
    ).subscribe());
  }

  /**
   * Everyone the application already holds, so the dialog can refuse to add one of them a second
   * time. Read from the KYB result the step already loads plus the step's own rows; direct sale has
   * no KYB result, which leaves just the rows.
   *
   * `editing` is the row the dialog was opened on and is left out, so reopening a saved row does not
   * report it as a duplicate of itself.
   */
  private peopleAlreadyOnApplication(editing: IndividualOrEntityFormGroupType): ExistingPersonRef[] {
    const people: ExistingPersonRef[] = [];

    for (const entity of this.kybResult?.entities ?? []) {
      // A guarantor the broker removed here is on its way off the application and the step save will
      // soft-delete it, so its people are not duplicates - the same reasoning that stops
      // adoptUnlistedGuarantorEntities putting the entity itself straight back on the form.
      if (this.isRemovedGuarantor(entity)) {
        continue;
      }
      for (const person of entity.people) {
        people.push({
          givenName: person.givenName,
          middleName: person.middleName,
          surname: person.surname,
          dateOfBirth: person.dateOfBirth,
          location: `a key person of ${entity.entityName}`,
        });
      }
    }

    for (const added of this.kybResult?.addedParties ?? []) {
      const owner = (this.kybResult?.entities ?? [])
        .find(entity => entity.commercialEntityId === added.commercialEntityId);
      // Parties go with the guarantor they belong to, so a removed owner takes its trustees and
      // partners out of the comparison too.
      if (owner && this.isRemovedGuarantor(owner)) {
        continue;
      }
      const ownerName = owner?.entityName;
      for (const party of added.parties) {
        if (party.type !== 'Individual') {
          continue;
        }
        people.push({
          givenName: party.firstName,
          middleName: party.middleName,
          surname: party.lastName,
          dateOfBirth: party.dateOfBirth,
          location: ownerName ? `${party.kind.toLowerCase()} of ${ownerName}` : `an added ${party.kind.toLowerCase()}`,
        });
      }
    }

    for (const row of this.formArray.controls) {
      if (row === editing || row.controls.type?.value !== 'Individual') {
        continue;
      }
      people.push({
        givenName: row.controls.firstName?.value ?? null,
        middleName: row.controls.middleName?.value ?? null,
        surname: row.controls.lastName?.value ?? null,
        dateOfBirth: row.controls.dob?.value?.format('YYYY-MM-DD') ?? null,
        location: 'another additional guarantor on this step',
      });
    }

    return people;
  }

  // ---------------------------------------------------------------- entity company check

  /**
   * Persist the guarantor and start its company check. The endpoint is idempotent for an unchanged
   * ABN/ACN and the run is subject to the same per-entity cooldown as every other KYB run, so a
   * broker who re-enters a company they discarded reuses the completed run rather than paying for
   * a second one.
   */
  selectSigners(formGroup: IndividualOrEntityFormGroupType): void {
    if (!this.kybInitiateCorporatePartyFn || this.applicationId == null) {
      return;
    }
    const abn = formGroup.controls.abn?.value || null;
    const acn = formGroup.controls.acn?.value || null;
    if (!abn && !acn) {
      return;
    }
    const key = `${abn ?? ''}|${acn ?? ''}`;
    if (this.initiatedIdentifiers.get(formGroup) === key) {
      return;
    }
    // Marked before the call so a burst of form events cannot fire twice, then released on failure:
    // nothing else creates the entity, and a key left behind would strand the row with no way to
    // reach its key people.
    this.initiatedIdentifiers.set(formGroup, key);
    this.subscriptions.push(this.kybInitiateCorporatePartyFn({
      applicationId: this.applicationId,
      role: 'guarantor',
      legalName: formGroup.controls.organisation?.value?.name ?? null,
      abn,
      acn,
      entityType: formGroup.controls.organisationType?.value?.type ?? null,
    }).pipe(
      tap(result => {
        formGroup.controls.commercialEntityId?.setValue(result.commercialEntityId);
        this.loadKybResult();
      }),
      catchError(() => {
        if (this.initiatedIdentifiers.get(formGroup) === key) {
          this.initiatedIdentifiers.delete(formGroup);
        }
        return EMPTY;
      }),
    ).subscribe());
  }

  /**
   * A trust or partnership guarantor is persisted as soon as its type is known, rather than behind a
   * button: its key people are the trustees/partners entered against it, and they cannot be attached
   * to it until it has an id, so the section that collects them has nothing to hang off before then.
   */
  private ensurePartiesEntity(formGroup: IndividualOrEntityFormGroupType): void {
    if (this.partiesEntityType(formGroup) && !this.entityFor(formGroup)) {
      this.selectSigners(formGroup);
    }
  }

  /** Retry from the panel shown while the guarantor entity does not exist yet. */
  retryPartiesEntity(formGroup: IndividualOrEntityFormGroupType): void {
    this.initiatedIdentifiers.delete(formGroup);
    this.selectSigners(formGroup);
  }

  private loadKybResult(): void {
    // 0 is what the pages bind before the first save assigns an id, so it means "no application yet"
    // rather than a real one to read.
    if (!this.kybResultFn || !this.applicationId || this.loadingKybApplicationId === this.applicationId) {
      return;
    }
    const applicationId = this.applicationId;
    this.loadingKybApplicationId = applicationId;
    this.subscriptions.push(this.kybResultFn(applicationId).pipe(
      tap(result => {
        this.applyKybResult(result);
      }),
      catchError(() => EMPTY),
      finalize(() => {
        if (this.loadingKybApplicationId === applicationId) {
          this.loadingKybApplicationId = null;
        }
      }),
    ).subscribe());
  }

  private applyKybResult(result: KybResult): void {
    this.kybResult = result;
    for (const entity of result.entities.filter(candidate => candidate.mode === 'MANUAL')) {
      const currentPeople = this.manualPeopleByEntity.get(entity.commercialEntityId);
      const people = this.manualPeopleEnteredEntityIds.has(entity.commercialEntityId)
        ? (currentPeople ?? [])
        : (currentPeople?.length ? currentPeople : entity.manualPeople ?? []);
      entity.manualPeople = people;
      this.manualPeopleByEntity.set(entity.commercialEntityId, people);
      this.manualValidityByEntity.set(
        entity.commercialEntityId,
        people.length > 0 && people.some(person => person.isSigner),
      );
    }
    this.reconcileEntityIds();
    this.adoptUnlistedGuarantorEntities();
    this.changes();
  }

  /**
   * Put a guarantor entity that exists on the application, but has no row here, onto the form.
   *
   * The step is populated once from the application the page resolved on load, and never re-reads it,
   * so an entity the server created later in the same session is invisible until the draft is
   * reopened. That is how the previous business arrives: it is materialised from the ABN captured on
   * step 1, several steps before this one is shown.
   *
   * Runs after reconcileEntityIds, which links the rows the broker did type, so only genuinely
   * unrepresented entities are left. Matching is on ABN/ACN - the same identity the save reconciles
   * on - so this cannot double up a company that is already on the form.
   *
   * A corporate trustee/partner is left out: it inherits the guarantor flag from the trust or
   * partnership it stands behind, but it is already on screen inside that guarantor's parties
   * section, so adopting it would show the same company twice. So is one the broker removed here,
   * which is absent for the opposite reason and is the step save's to soft-delete.
   */
  private adoptUnlistedGuarantorEntities(): void {
    // Nothing to adopt onto: the single-entry host shows one row, for the guarantor being added.
    if (this.singleEntry) {
      return;
    }
    for (const entity of this.kybResult?.entities ?? []) {
      if (!kybIsAdditionalGuarantor(entity) || kybIsCorporateParty(entity)
        || this.isEntityListed(entity) || this.isRemovedGuarantor(entity)) {
        continue;
      }
      const acn = kybDigitsOnly(entity.acn)?.padStart(9, '0') ?? '';
      this.addGuarantor({
        type: 'Entity',
        organisationType: EntityTypeValueOptions.find(option => option.type === entity.entityType)
          ?? EntityTypeCompanyOption,
        abn: entity.abn ?? '',
        acn,
        organisation: {
          abn: entity.abn ?? '',
          acn,
          name: entity.entityName,
          displayName: entity.entityName,
        },
      });
      // addGuarantor builds the row from the value alone, so the link to the persisted entity has to
      // be set here: without it the row offers to start a check that is already running. Safe to do
      // after the push because the valueChanges handler that acts on the row is delayed.
      const added = this.formArray.controls[this.formArray.controls.length - 1];
      added.controls.commercialEntityId?.setValue(entity.commercialEntityId, {emitEvent: false});
    }
  }

  private isEntityListed(entity: KybEntityResult): boolean {
    return this.formArray.controls.some(formGroup => {
      if (formGroup.controls.type?.value !== 'Entity') {
        return false;
      }
      if (formGroup.controls.commercialEntityId?.value === entity.commercialEntityId) {
        return true;
      }
      return this.entityIdentifiersMatch(formGroup, entity);
    });
  }

  private entityIdentifiersMatch(
    formGroup: IndividualOrEntityFormGroupType,
    entity: KybEntityResult,
  ): boolean {
    const abn = kybDigitsOnly(formGroup.controls.abn?.value);
    const entityAbn = kybDigitsOnly(entity.abn);
    const acn = kybDigitsOnly(formGroup.controls.acn?.value)?.padStart(9, '0') ?? null;
    const entityAcn = kybDigitsOnly(entity.acn)?.padStart(9, '0') ?? null;
    return (!!abn && abn === entityAbn) || (!!acn && acn === entityAcn);
  }

  /**
   * Re-link rows to their persisted entity after a reload, when the form was rebuilt from the saved
   * guarantors and so carries no entity id yet. Matched on ABN/ACN, which is what identifies the
   * company on both sides.
   *
   * Corporate trustees/partners are skipped for the same reason they are not adopted: one carries
   * the guarantor flag of its owner, so a row for the same company would otherwise take over the
   * entity belonging to that guarantor's parties section.
   */
  private reconcileEntityIds(): void {
    for (const formGroup of this.formArray.controls) {
      if (formGroup.controls.type?.value !== 'Entity' || formGroup.controls.commercialEntityId?.value) {
        continue;
      }
      const entity = (this.kybResult?.entities ?? []).find(e =>
        e.isGuarantor && !kybIsCorporateParty(e) && this.entityIdentifiersMatch(formGroup, e));
      if (entity) {
        formGroup.controls.commercialEntityId?.setValue(entity.commercialEntityId, {emitEvent: false});
      }
    }
  }

  entityFor(formGroup: IndividualOrEntityFormGroupType): KybEntityResult | null {
    const commercialEntityId = formGroup.controls.commercialEntityId?.value;
    if (commercialEntityId == null) {
      return null;
    }
    return (this.kybResult?.entities ?? []).find(e => e.commercialEntityId === commercialEntityId) ?? null;
  }

  entityStatus(formGroup: IndividualOrEntityFormGroupType): KybDisplayStatus | null {
    const entity = this.entityFor(formGroup);
    return entity?.mode === 'MANUAL' ? 'resolved' : entity ? kybDisplayStatus(entity.status) : null;
  }

  startManualKyb(formGroup: IndividualOrEntityFormGroupType): void {
    const entity = this.entityFor(formGroup);
    if (!entity) return;
    this.persistManualKyb(entity);
  }

  startManualKybById(commercialEntityId: number): void {
    const entity = this.kybResult?.entities.find(candidate => candidate.commercialEntityId === commercialEntityId);
    if (!entity) return;
    this.persistManualKyb(entity);
  }

  private persistManualKyb(entity: KybEntityResult): void {
    if (this.kybSetManualModeFn && this.applicationId != null) {
      this.subscriptions.push(this.kybSetManualModeFn(
        this.applicationId,
        entity.commercialEntityId,
      ).pipe(
        catchError(() => EMPTY),
      ).subscribe(result => this.onKybResolved(result)));
      return;
    }
    entity.mode = 'MANUAL';
    const people = this.manualPeopleByEntity.get(entity.commercialEntityId) ?? entity.manualPeople ?? [];
    this.manualPeopleByEntity.set(entity.commercialEntityId, people);
    this.manualValidityByEntity.set(
      entity.commercialEntityId,
      people.length > 0 && people.some(person => person.isSigner),
    );
    this.changes();
  }

  manualPeopleFor(entity: KybEntityResult): KybManualPerson[] {
    return this.manualPeopleByEntity.get(entity.commercialEntityId) ?? entity.manualPeople ?? [];
  }

  onManualPeopleChange(entity: KybEntityResult, people: KybManualPerson[]): void {
    this.recordManualPeople(entity.commercialEntityId, people);
    entity.manualPeople = people;
    this.changes();
  }

  private recordManualPeople(commercialEntityId: number, people: KybManualPerson[]): void {
    if (people.length > 0) {
      this.manualPeopleEnteredEntityIds.add(commercialEntityId);
    }
    this.manualPeopleByEntity.set(commercialEntityId, people);
  }

  onManualValidityChange(entity: KybEntityResult, valid: boolean): void {
    this.manualValidityByEntity.set(entity.commercialEntityId, valid);
    this.changes();
  }

  onNestedManualPeopleChange(event: { commercialEntityId: number; people: KybManualPerson[] }): void {
    const entity = this.kybResult?.entities.find(
      candidate => candidate.commercialEntityId === event.commercialEntityId,
    );
    this.recordManualPeople(event.commercialEntityId, event.people);
    if (entity) entity.manualPeople = event.people;
    this.changes();
  }

  onNestedManualValidityChange(event: { commercialEntityId: number; valid: boolean }): void {
    this.manualValidityByEntity.set(event.commercialEntityId, event.valid);
    this.changes();
  }

  manualIdDocumentUploads(): IndividualIdDocumentUpload[] {
    return this.manualPeopleComponents?.toArray().flatMap(component => component.idDocumentUploads()) ?? [];
  }

  businessNameFor(formGroup: IndividualOrEntityFormGroupType): string {
    return this.entityFor(formGroup)?.entityName
      ?? formGroup.controls.organisation?.value?.name
      ?? 'this company';
  }

  /**
   * Read the persisted state of every key person this step shows a tick for into the selection maps.
   *
   * Both steps write through the same Key People model and the save is a full sync, so a stakeholder
   * this step never emits is taken as deselected. Without seeding, reopening an application would
   * clear the guarantor/signer roles saved against an additional guarantor's key people even though
   * the table still shows them ticked.
   */
  private seedSelections(): void {
    for (const formGroup of this.formArray.controls) {
      for (const entity of this.tickedEntitiesFor(formGroup)) {
        // Corporate trustees/partners behind a trust or partnership are signed for as companies.
        const entityType = this.partiesEntityType(formGroup) ? 'company' : this.signerEntityTypeFor(formGroup, entity);
        for (const person of entity.people) {
          if (this.seededStakeholderIds.has(person.stakeholderId)) {
            continue;
          }
          this.seededStakeholderIds.add(person.stakeholderId);
          if (kybIsSoleTraderRoleLocked(entityType)) {
            person.isGuarantor = true;
            person.isSigner = true;
            this.soleTraderStakeholderIds.add(person.stakeholderId);
          }
          this.selectionByStakeholder.set(person.stakeholderId, {
            stakeholderId: person.stakeholderId,
            isGuarantor: person.isGuarantor,
            isSigner: person.isSigner,
          });
          // Same reading as the table's left column, so the people reported as selected are the ones
          // shown as selected; the save only keeps the roles of a selected stakeholder.
          if (kybResolveSelected(entityType, person)) {
            this.selectedStakeholderIds.add(person.stakeholderId);
          }
        }
      }
    }
  }

  /** The signer table follows the row's own type so a sole trader gets its locked roles; any other entity signs as a company. */
  signerEntityTypeFor(formGroup: IndividualOrEntityFormGroupType, entity: KybEntityResult): EntityTypes {
    return this.isSoleTraderGuarantor(formGroup, entity) ? 'sole-trader' : 'company';
  }

  isSoleTraderGuarantor(formGroup: IndividualOrEntityFormGroupType, entity: KybEntityResult): boolean {
    return kybIsSoleTraderRoleLocked(formGroup.controls.organisationType?.value?.type ?? entity.entityType);
  }

  /** Stakeholders whose guarantor tick the Key People step holds, so the tables lock it. */
  get guarantorLockedStakeholderIds(): ReadonlySet<number> {
    return this.guarantorLockedIds;
  }

  /**
   * Carry a guarantor tick made on the Key People step onto the same individual's rows here, and
   * lock it: the flag is the individual's rather than the row's, so undoing it is that step's to do.
   * See kybGuarantorsHeldByKeyPeople for why the two tables cannot answer independently.
   */
  private mirrorKeyPeopleGuarantors(): void {
    const wasLocked = this.guarantorLockedIds;
    const shown = this.renderedEntities();
    this.guarantorLockedIds = kybGuarantorsHeldByKeyPeople(
      shown,
      this.kybResult?.entities ?? [],
      this.keyPeopleSelections,
    );
    for (const entity of shown) {
      for (const person of entity.people) {
        const locked = this.guarantorLockedIds.has(person.stakeholderId);
        // A row that was never mirrored carries the broker's own tick, which is left as they left it.
        if (locked || wasLocked.has(person.stakeholderId)) {
          this.applyMirroredGuarantor(person, locked);
        }
      }
    }
  }

  private applyMirroredGuarantor(person: KybPerson, held: boolean): void {
    // A sole trader stays a guarantor even when Key People releases the same person.
    const isGuarantor = held || this.soleTraderStakeholderIds.has(person.stakeholderId);
    person.isGuarantor = isGuarantor;
    const current = this.selectionByStakeholder.get(person.stakeholderId);
    this.selectionByStakeholder.set(person.stakeholderId, {
      stakeholderId: person.stakeholderId,
      isGuarantor,
      isSigner: current?.isSigner ?? person.isSigner,
    });
    if (isGuarantor) {
      // The save keeps the roles of a selected stakeholder only, so a guarantor has to be reported
      // as selected as well - which is also what the row's left column now shows.
      this.selectedStakeholderIds.add(person.stakeholderId);
    }
  }

  private renderedEntities(): KybEntityResult[] {
    return this.formArray.controls.flatMap(formGroup => this.tickedEntitiesFor(formGroup));
  }

  /**
   * The entities whose key people this step renders ticks for: a guarantor entity itself, or - when
   * it is a trust or partnership, which has no key people of its own - the corporate
   * trustees/partners standing behind it.
   */
  private tickedEntitiesFor(formGroup: IndividualOrEntityFormGroupType): KybEntityResult[] {
    if (this.partiesEntityType(formGroup)) {
      return this.partiesByFormGroup.get(formGroup)?.corporateParties ?? [];
    }
    const entity = this.entityFor(formGroup);
    return entity ? [entity] : [];
  }

  onPersonSelectedChange(event: {person: KybPerson, selected: boolean}): void {
    if (event.selected) {
      this.selectedStakeholderIds.add(event.person.stakeholderId);
    } else {
      this.selectedStakeholderIds.delete(event.person.stakeholderId);
    }
    this.changes();
  }

  onPersonRoleChange(event: KybPersonRoleSelection): void {
    this.selectionByStakeholder.set(event.stakeholderId, event);
    this.changes();
  }

  onKybResolved(result: KybResult): void {
    this.applyKybResult(result);
  }

  /**
   * Apply the Key People signer rules to each guarantor entity, so an additional guarantor names
   * signers the same way the applicant's own company does. The guarantor rules are skipped: who
   * guarantees an additional guarantor is the broker's call, so the ticks on this step's tables are
   * recorded rather than required.
   */
  private recomputeKybValidation(): void {
    const selections = [...this.selectionByStakeholder.values()];
    const errors: string[] = [];
    const warnings: string[] = [];
    for (const formGroup of this.formArray.controls) {
      for (const entity of this.validatableCompaniesFor(formGroup)) {
        const result = kybValidateCompanyKeyPeople(entity, 'company', selections, {skipGuarantorRules: true});
        errors.push(...result.errors);
        warnings.push(...result.warnings);
      }
    }
    this.entityErrors = errors;
    this.warningsChange.emit(warnings);
  }

  /**
   * The companies one guarantor row is answerable for: the guarantor itself when it is a company,
   * or - when it is a trust or partnership, which has no key people of its own - the corporate
   * trustees/partners standing behind it, whose officers supply the signers.
   */
  private validatableCompaniesFor(formGroup: IndividualOrEntityFormGroupType): KybEntityResult[] {
    if (this.partiesEntityType(formGroup)) {
      return (this.partiesByFormGroup.get(formGroup)?.corporateParties ?? [])
        .filter(entity => entity.mode !== 'MANUAL');
    }
    const entity = this.entityFor(formGroup);
    return entity
      && entity.mode !== 'MANUAL'
      && entity.entityType === 'company'
      && kybDisplayStatus(entity.status) === 'resolved'
      && entity.people.length
      ? [entity]
      : [];
  }

  validate(): ValidationErrors | null {
    if (this.formArray.valid) {
      return null;
    }
    const details = [...this.entityErrors];
    this.formArray.controls.forEach((formGroup, index) => {
      const prefix = `Additional guarantor ${index + 1}`;
      for (const [name, control] of Object.entries(formGroup.controls)) {
        if (control.hasError('required')) {
          details.push(`${prefix}: ${name} is required.`);
        } else if (name === 'acn' && control.hasError('minlength')) {
          details.push(`${prefix}: ACN must contain 9 digits.`);
        }
      }
      if (!this.isEntryComplete(formGroup) && !Object.values(formGroup.controls).some(control => control.invalid)) {
        details.push(`${prefix} is incomplete.`);
      }
    });
    return {guarantorValidation: {details: details.length ? details : ['Please complete the additional guarantor.']}};
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  kybErrors(): string[] {
    return this.showValidation ? this.entityErrors : [];
  }

  mark(): void {
    this.showValidation = true;
    this.formGroup.markAllAsTouched();
    this.markObservable.next(true);
    this.manualPeopleComponents?.forEach(component => component.mark());
  }
}
